import { safeUrl } from "./safeUrl";
import { apiFetch } from "./api";
import { Capacitor } from "@capacitor/core";

const ERROR_QUEUE_KEY = "trybeup_error_queue";
const MAX_QUEUE_SIZE = 20;
const FLUSH_INTERVAL = 30000;

// Tag every crash report with the running build + platform so the Errors
// tab / Sentry / crash-alert email say "v1.1.x on ios" instead of leaving
// us guessing (we hit exactly this gap chasing the admin-page crash on
// 2026-06-13). __APP_VERSION__ is the package.json semver injected at build
// time (see vite.config.ts); platform is ios / android / web.
const APP_VERSION: string = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "";
let PLATFORM = "web";
try { PLATFORM = Capacitor.getPlatform(); } catch { /* default web */ }

interface ErrorReport {
  message: string;
  stack?: string;
  source?: string;
  timestamp: string;
  url: string;
  userAgent: string;
  appVersion?: string;
  platform?: string;
}

let initialized = false;

// Benign errors we deliberately DON'T report to /api/client-errors.
// Reporting them pollutes the in-app Errors tab, fires crash-alert
// emails to the founder, and creates noise issues in Sentry — for
// errors that aren't bugs.
//
// "Lock was stolen by another request" — an AbortError (DOMException
// code 20) thrown by the Web Locks API that the Supabase/GoTrue auth
// client (@supabase/auth-js) uses to coordinate token access across
// tabs/contexts. When a second context acquires the auth lock, the
// first's pending acquisition is intentionally stolen and its promise
// rejects with this message. It's part of the lock protocol, not a
// failure — the auth operation is retried. Surfaced as an
// unhandledrejection because the rejection isn't always awaited.
// Confirmed 2026-06-13: in-app Errors tab showed 16 Phone/Browser, 0
// Server for this message — a purely client-side, non-actionable event.
const IGNORED_ERROR_PATTERNS: RegExp[] = [
  /Lock was stolen by another request/i,
  // @supabase/auth-js v2.70+ changed the wording from "Lock was
  // stolen…" to "Lock broken by another request with the 'steal'
  // option." Same protocol behavior, new string. Re-emerged as a
  // founder crash-alert flood 2026-06-26 (issues dc18c12d, 65d2c193)
  // because the old regex didn't match the new wording. Both kept so
  // the filter survives a future downgrade.
  /Lock broken by another request/i,
  // The DOMException name for the same Web Locks abort, in case the
  // message is ever blank but the name survives.
  /AbortError.*lock/i,
  // Dynamic-import / lazy-chunk load failures. These are ALREADY self-healed
  // by the one-reload-per-minute handler in main.tsx (vite:preloadError +
  // the unhandledrejection backstop), which reloads the device onto a fresh
  // bundle and recovers — so the user never sees a broken app. Reporting them
  // anyway spammed the founder with "New crash detected · Importing a module
  // script failed" alerts for a non-actionable, auto-recovered event (crash
  // e6ae6367, build 1.1.626 · ios, 2026-07-08). Same wording set as
  // main.tsx's CHUNK_ERROR_RE. If the reload ever fails to recover, the
  // repeated failure still surfaces through other signals (blank screen
  // reports), so suppressing the alert here is safe.
  /importing a module script failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  // Benign network blips. WebKit/iOS reports EVERY failed or aborted fetch as a
  // bare "Load failed" (Chromium: "Failed to fetch"); a fire-and-forget request
  // or a backgrounded app losing connectivity mid-flight surfaces as an
  // unhandledrejection with this exact message. Not an actionable crash —
  // reporting it created a "regressed" Sentry issue and emailed the founder a
  // "crash" for every network hiccup (issue da37773b, v1.1.600 ios, 2026-07-24).
  // Anchored to the WHOLE message so a real error that merely CONTAINS these
  // words still reports. (Distinct from chunkReload's deliberate refusal to
  // *reload* on a bare "Load failed" — here we only decline to *report* it,
  // which is safe. The backend /api/client-errors has a matching guard for
  // clients still on an old bundle.)
  /^(?:TypeError:\s*)?Load failed$/i,
  /^(?:TypeError:\s*)?Failed to fetch$/i,
  /^NetworkError when attempting to fetch resource\.?$/i,
  /^Network request failed$/i,
  /^The network connection was lost\.?$/i,
  /^The request timed out\.?$/i,
  /^The Internet connection appears to be offline\.?$/i,
  // The browser's redacted cross-origin error: a script from another origin
  // (Google Analytics, or code an in-app browser injects) threw, and all we're
  // told is "Script error." — no file, line or stack. Our own bundle is
  // same-origin and always reports full detail, so this is never ours
  // (Sentry, /brand on iPhone Safari, v1.3.151, 2026-10-07).
  /^Script error\.?$/i,
];

function _isIgnored(message: string | undefined | null): boolean {
  if (!message) return false;
  return IGNORED_ERROR_PATTERNS.some((re) => re.test(message));
}

function queueError(report: ErrorReport) {
  try {
    const raw = localStorage.getItem(ERROR_QUEUE_KEY);
    const queue: ErrorReport[] = raw ? JSON.parse(raw) : [];
    queue.push(report);
    if (queue.length > MAX_QUEUE_SIZE) queue.splice(0, queue.length - MAX_QUEUE_SIZE);
    localStorage.setItem(ERROR_QUEUE_KEY, JSON.stringify(queue));
  } catch {}
}

async function flushErrors() {
  try {
    const raw = localStorage.getItem(ERROR_QUEUE_KEY);
    if (!raw) return;
    const queue: ErrorReport[] = JSON.parse(raw);
    if (queue.length === 0) return;

    localStorage.removeItem(ERROR_QUEUE_KEY);

    await apiFetch("/api/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ errors: queue }),
    }).catch(() => {
      try {
        const existing = localStorage.getItem(ERROR_QUEUE_KEY);
        const current: ErrorReport[] = existing ? JSON.parse(existing) : [];
        const merged = [...queue, ...current].slice(-MAX_QUEUE_SIZE);
        localStorage.setItem(ERROR_QUEUE_KEY, JSON.stringify(merged));
      } catch {}
    });
  } catch {}
}

export function initErrorTracking() {
  if (initialized) return;
  initialized = true;

  window.addEventListener("error", (event) => {
    const message = event.message || "Unknown error";
    if (_isIgnored(message)) return;
    queueError({
      message,
      stack: event.error?.stack,
      source: `${event.filename}:${event.lineno}:${event.colno}`,
      timestamp: new Date().toISOString(),
      url: safeUrl(window.location.href),
      userAgent: navigator.userAgent,
      appVersion: APP_VERSION,
      platform: PLATFORM,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message = reason?.message || String(reason) || "Unhandled promise rejection";
    // Drop benign, non-actionable rejections (e.g. the GoTrue Web Locks
    // "Lock was stolen by another request" AbortError) before they reach
    // the founder's inbox + Errors tab. See IGNORED_ERROR_PATTERNS.
    if (_isIgnored(message) || _isIgnored(reason?.name)) return;
    queueError({
      message,
      stack: reason?.stack,
      source: "unhandledrejection",
      timestamp: new Date().toISOString(),
      url: safeUrl(window.location.href),
      userAgent: navigator.userAgent,
      appVersion: APP_VERSION,
      platform: PLATFORM,
    });
  });

  setInterval(flushErrors, FLUSH_INTERVAL);
  window.addEventListener("beforeunload", flushErrors);
}

export function reportError(error: Error, context?: string) {
  // Apply the same ignore-list the window/rejection listeners use. Without this,
  // benign self-healing errors that reach reportError directly — most notably a
  // lazy-chunk "Importing a module script failed" caught by the ErrorBoundary
  // during render — bypassed the filter and spammed the founder's crash alerts
  // (audit 2026-07-19).
  if (_isIgnored(error?.message) || _isIgnored(error?.name)) return;
  queueError({
    message: error.message,
    stack: error.stack,
    source: context || "manual",
    timestamp: new Date().toISOString(),
    url: safeUrl(window.location.href),
    userAgent: navigator.userAgent,
    appVersion: APP_VERSION,
    platform: PLATFORM,
  });
}
